// markup/pdfMarkup.mjs — a plan's markup as drawing instructions for the PDF (pure).
// ---------------------------------------------------------------------------
// 2026-09-15 — Brett: "do the pdf too" (after the shared report started showing
// the measured areas, the Legend stamp and trade-coloured pins). The PDF is
// built with jsPDF, not SVG, so the markup is turned into a short list of plain
// instructions here — shapes, measurement chips, notes, the legend — and the
// report template draws them. The labels and totals come from the same measure
// and take-off code as the app, so the PDF reads the same numbers as the plan.
//
// Every position is NORMALISED (0..1 across the plan) — the template maps it onto
// wherever the plan sits on the page. A legend keeps its natural-pixel sizes: it
// is a stamp that scales with the plan.
//
// v0.305.0 (2026-11-02) — Brett, after the client's web report lost its m² chips: "hide them on the pdf
// too". One PDF serves the office download and the client's link, so by default the PDF draws the marks
// with no area / perimeter / length chip and the Legend with no totals column — the take-off figures are
// ours, not the client's. { figures: true } brings them back for a caller that wants them.
// ---------------------------------------------------------------------------

import { readOnlyMarkup } from './readOnly.mjs';
import { measureArea, measureLength } from './measure.mjs';
import { centroidPx, rectXYWH } from './model.mjs';
import { LEGEND_TITLE, legendLayout, readableInk, cleanColor } from './legend.mjs';
import { legendTotals } from './takeoff.mjs';

/** '#rrggbb' / '#rgb' → [r, g, b]; anything else → the fallback. */
export function hexRgb(hex, fallback = [37, 99, 235]) {
  const c = cleanColor(hex);
  if (!c) return fallback;
  let h = c.slice(1);
  if (h.length === 3) h = h.split('').map((x) => x + x).join('');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
}

const pairs = (points) => (points || []).map((p) => [p.x, p.y]);

export function markupPdfOps(doc, W, H, { figures = false } = {}) {
  const m = readOnlyMarkup(doc, { figures });
  if (!m.count || !(W > 0) || !(H > 0)) return [];

  const legendKeys = new Set();
  for (const o of m.objects) if (o.type === 'legend') for (const r of (o.rows || [])) legendKeys.add(r.key);
  const totals = legendKeys.size && !m.hideFigures ? legendTotals(m.objects, W, H, m.calibration, m.unit, [...legendKeys]) : {};

  const ops = [];
  for (const o of m.objects) {
    const s = o.style || {};
    const stroke = hexRgb(s.stroke, [232, 101, 10]);
    switch (o.type) {
      case 'measure': {
        const isArea = o.measureKind === 'area';
        ops.push({
          op: 'poly', pts: pairs(o.points), closed: isArea, stroke,
          fill: isArea ? hexRgb(s.fill || s.stroke, stroke) : null,
          fillOpacity: isArea ? (s.fillOpacity != null ? s.fillOpacity : 0.1) : 0,
        });
        if (m.hideFigures) break;
        const res = isArea
          ? measureArea(o.points, W, H, m.calibration, m.unit)
          : measureLength(o.points, W, H, m.calibration, m.unit);
        const at = isArea
          ? (() => { const c = centroidPx(o, W, H); return [c.x / W, c.y / H]; })()
          : [o.points[o.points.length - 1].x, o.points[o.points.length - 1].y];
        ops.push({
          op: 'label', at,
          lines: isArea ? [res.areaLabel, `P ${res.perimLabel}`] : [res.label],
          bg: stroke, ink: hexRgb(readableInk(s.stroke), [255, 255, 255]),
        });
        break;
      }
      case 'polygon':
        ops.push({ op: 'poly', pts: pairs(o.points), closed: true, stroke, fill: s.fill ? hexRgb(s.fill, stroke) : null, fillOpacity: s.fillOpacity || 0 });
        break;
      case 'rect': {
        const r = rectXYWH(o);
        ops.push({
          op: 'poly', pts: [[r.x, r.y], [r.x + r.w, r.y], [r.x + r.w, r.y + r.h], [r.x, r.y + r.h]],
          closed: true, stroke, fill: s.fill ? hexRgb(s.fill, stroke) : null, fillOpacity: s.fillOpacity || 0,
        });
        break;
      }
      case 'line':
      case 'polyline':
      case 'freehand':
        ops.push({ op: 'poly', pts: pairs(o.points), closed: false, stroke, fill: null, fillOpacity: 0 });
        break;
      case 'arrow': {
        const pts = pairs(o.points);
        ops.push({ op: 'poly', pts, closed: false, stroke, fill: null, fillOpacity: 0 });
        if (pts.length >= 2) ops.push({ op: 'arrowhead', from: pts[pts.length - 2], to: pts[pts.length - 1], color: stroke });
        break;
      }
      case 'text':
        if (o.text && o.points[0]) ops.push({ op: 'text', at: [o.points[0].x, o.points[0].y], text: String(o.text), color: hexRgb(s.fill || s.stroke, stroke) });
        break;
      case 'legend': {
        const L = legendLayout(o);
        ops.push({
          op: 'legend', at: [o.points[0].x, o.points[0].y], title: LEGEND_TITLE,
          fsPx: L.fs, wPx: L.w, hPx: L.h, padPx: L.pad, titleHPx: L.titleH, rowHPx: L.rowH,
          swatchPx: L.swatch, labelXPx: L.labelX, totalsXPx: L.totalsX, showTotals: L.totals,
          rows: L.rows.map((r) => ({ label: r.label, color: hexRgb(r.color), topPx: r.top, total: totals[r.key] || '—' })),
        });
        break;
      }
      default:
        break;
    }
  }
  return ops;
}
